import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sparkles, ChevronDown, Check, Search, Lock } from 'lucide-react';
import { cn } from '../../lib/utils';
import { providerMarkFor } from './providerMarks';

/**
 * Model picker dropdown — used in two places inside AIHub:
 * 1. The composer toolbar (size="sm")
 * 2. The CommandHero hero landing (size="md")
 *
 * Shows the Auto/Optimize entry pinned to the top, then provider-grouped
 * model lists with tier badges (Fast $ / Balanced $$ / Premium $$$ / FREE),
 * a search filter, and an upgrade nudge on the free plan.
 *
 * Extracted from pages/AIHub.jsx to make this 225-line dropdown indepedently
 * testable AND to slim the page file down. All state still lives in the
 * parent — the picker is purely presentational, driven by props.
 */
export function ModelPicker({
  size = 'sm',
  chosenModel,
  setChosenModel,
  activeModel,
  models,
  modelSearch,
  setModelSearch,
  groupedModels,
  tier,
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size={size}
          variant="ghost"
          className={cn(
            'gap-1.5 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-black/5 dark:hover:bg-white/5',
            size === 'sm' ? 'h-8 px-2' : 'h-9 px-3',
          )}
          data-testid="ai-hub-model-picker"
        >
          {(() => {
            if (chosenModel === 'auto') {
              return <Sparkles className="w-3.5 h-3.5 text-violet-500" />;
            }
            const Mark = providerMarkFor(chosenModel);
            return Mark ? <Mark className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />;
          })()}
          <span className="text-xs truncate max-w-[110px] sm:max-w-[180px]">
            {chosenModel === 'auto' ? 'Auto' : (activeModel?.name || chosenModel || 'Select model')}
          </span>
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="top"
        className="w-80 max-h-[60vh] overflow-y-auto bg-white dark:bg-zinc-950 border-zinc-200 dark:border-white/10 text-zinc-800 dark:text-zinc-200"
      >
        <DropdownMenuLabel className="text-xs text-zinc-500 px-2">
          {models.length} models available
        </DropdownMenuLabel>
        <div className="px-2 pb-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={modelSearch}
              onChange={(e) => setModelSearch(e.target.value)}
              placeholder="Search models…"
              className="h-8 pl-7 text-xs bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-white/10"
              onClick={(e) => e.stopPropagation()}
              data-testid="ai-hub-model-search"
            />
          </div>
        </div>
        <DropdownMenuSeparator className="bg-zinc-200 dark:bg-white/5" />
        {/* Auto / Optimize — pinned at the top. Picks the best model per
            message server-side via _auto_route_model so users don't have
            to mode-switch all day. */}
        {groupedModels.auto && (
          <div className="px-1 pb-1">
            <button
              onClick={() => setChosenModel('auto')}
              className={cn(
                'w-full text-left flex items-start gap-2 px-2 py-2 rounded-md transition-colors',
                chosenModel === 'auto'
                  ? 'bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800'
                  : 'hover:bg-zinc-100 dark:hover:bg-zinc-900 border border-transparent',
              )}
              data-testid="ai-hub-model-auto"
            >
              <Sparkles className="w-4 h-4 mt-0.5 text-violet-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Auto</span>
                  <span className="text-[9px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded">BEST</span>
                </div>
                <p className="text-[11px] text-zinc-500 leading-snug mt-0.5">
                  Picks the best model for each message — fast for short Q&amp;A, premium for deep analysis.
                </p>
              </div>
              {chosenModel === 'auto' && <Check className="w-4 h-4 text-violet-500 flex-shrink-0" />}
            </button>
          </div>
        )}
        {/* Tier legend so users learn what $/$$/$$$ mean once and not again. */}
        <div className="px-2 py-1.5 text-[10px] text-zinc-500 dark:text-zinc-400 leading-snug border-y border-zinc-100 dark:border-white/5 flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-zinc-600 dark:text-zinc-300">Pricing:</span>
          <span><span className="font-bold text-emerald-600">$</span> Fast</span>
          <span>·</span>
          <span><span className="font-bold text-amber-600">$$</span> Balanced</span>
          <span>·</span>
          <span><span className="font-bold text-violet-600">$$$</span> Premium</span>
        </div>
        {groupedModels.groups.map((g) => (
          <div key={g.key} className="py-1">
            <div className="px-2 py-1 flex items-center gap-1.5">
              <g.Mark className="w-3 h-3" />
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
                {g.label}
              </span>
              <span className="text-[9px] font-mono text-zinc-400 dark:text-zinc-600">
                {g.models.length}
              </span>
            </div>
            {g.models.map((m) => (
              <ModelRow key={m.id} m={m} onPick={() => setChosenModel(m.id)} />
            ))}
          </div>
        ))}
        {groupedModels.other.length > 0 && (
          <div className="py-1 border-t border-zinc-200 dark:border-white/5">
            <div className="px-2 py-1 flex items-center gap-1.5">
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
                Other models
              </span>
              <span className="text-[9px] font-mono text-zinc-400 dark:text-zinc-600">
                {groupedModels.other.length}
              </span>
            </div>
            {groupedModels.other.map((m) => (
              <ModelRow key={m.id} m={m} onPick={() => setChosenModel(m.id)} />
            ))}
          </div>
        )}
        {tier === 'free' && (
          <div className="px-3 py-2.5 mt-1 border-t border-zinc-200 dark:border-white/5">
            <div className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400">
              <Lock className="w-3.5 h-3.5" />
              <span>Upgrade for 200+ premium models</span>
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Single row in the model picker — extracted because the same row markup
 * appears in two places (the grouped list and the "Other models" bucket).
 * Keeping it local instead of exporting because it's not useful outside.
 */
function ModelRow({ m, onPick }) {
  const Mark = providerMarkFor(m.id);
  return (
    <DropdownMenuItem
      onClick={onPick}
      className="flex items-start gap-2 py-1.5 cursor-pointer"
      data-testid={`ai-hub-model-${m.id}`}
    >
      {Mark ? <Mark className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> : <span className="w-3.5 h-3.5 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{m.name}</span>
          {m.badge && (
            <span className="text-[9px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded">
              {m.badge}
            </span>
          )}
          {m.is_free ? (
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
              FREE
            </span>
          ) : m.tier === 'auto' ? (
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 dark:text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded">
              AUTO
            </span>
          ) : (
            <span
              className={`ml-auto inline-flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded ${
                m.tier === 'fast' ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10'
                : m.tier === 'balanced' ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10'
                : m.tier === 'premium' ? 'text-violet-600 dark:text-violet-400 bg-violet-500/10'
                : 'text-zinc-500 bg-zinc-500/10'
              }`}
              title={
                m.tier === 'fast' ? `Fast tier (~$/msg) · ${m.credits_per_msg_estimate} credits per typical turn`
                : m.tier === 'balanced' ? `Balanced tier (~$$/msg) · ${m.credits_per_msg_estimate} credits per typical turn`
                : m.tier === 'premium' ? `Premium tier (~$$$/msg) · ${m.credits_per_msg_estimate} credits per typical turn`
                : 'Pricing unavailable'
              }
            >
              {m.tier === 'fast' ? '$' : m.tier === 'balanced' ? '$$' : m.tier === 'premium' ? '$$$' : '—'}
            </span>
          )}
        </div>
        <span className="text-[11px] text-zinc-500 line-clamp-1">
          {m.tagline || m.provider}
        </span>
      </div>
    </DropdownMenuItem>
  );
}

export default ModelPicker;
